import { z } from "zod";

import { Classification } from "@/src/models/constants/classifications";
import { BLOCK_SCHEMA_BY_KEY, type BlockMap } from "@/src/models/intake/blocks/block";
import { BlockKey } from "@/src/models/intake/blocks/block-types";

// Keep each schema behind a named output type so consumers do not expand the
// implementation of every block schema when checking a Section or review form.
type NullableBlockShape = { [K in BlockKey]: z.ZodType<BlockMap[K] | null, unknown> };

// Object.fromEntries erases key/schema correlation; all entries come from the registry.
const nullableBlockEntries: [BlockKey, z.ZodType<unknown>][] = Object.values(BlockKey).map((key) => [
  key,
  z.lazy(() => BLOCK_SCHEMA_BY_KEY[key]).nullable(),
]);
const nullableBlockShape = Object.fromEntries(nullableBlockEntries) as NullableBlockShape;

// A compact, key-specific view for generic readers. Classification schemas still
// determine required blocks and the existing union retains its validation behavior.
type ContentBlocks = { [K in BlockKey]?: BlockMap[K] | null };
export interface EnrichedContentBlocks extends ContentBlocks {}

// Referral
export const referralEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.PRIMARY_COVERAGE]: nullableBlockShape[BlockKey.PRIMARY_COVERAGE],
  [BlockKey.SECONDARY_COVERAGE]: nullableBlockShape[BlockKey.SECONDARY_COVERAGE],
  [BlockKey.PRIMARY_CARE_PROVIDER]: nullableBlockShape[BlockKey.PRIMARY_CARE_PROVIDER],
  [BlockKey.REFERRING_PROVIDER]: nullableBlockShape[BlockKey.REFERRING_PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.REFERRAL_INFORMATION]: nullableBlockShape[BlockKey.REFERRAL_INFORMATION],
  [BlockKey.PRIOR_AUTHORIZATION]: nullableBlockShape[BlockKey.PRIOR_AUTHORIZATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ReferralEnrichedContent = z.infer<typeof referralEnrichedContentSchema>;

export const referralBlockKeys = referralEnrichedContentSchema.keyof().options;
export type ReferralBlockKey = (typeof referralBlockKeys)[number];

// REFERRAL only: documentInformation is disabled by default and enabled per tenant in blocks.yaml.
export const referralWithDocumentInformationEnrichedContentSchema = z.object({
  ...referralEnrichedContentSchema.shape,
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
});

// Identification Card
export const identificationCardEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ID_CARD]: nullableBlockShape[BlockKey.ID_CARD],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type IdentificationCardEnrichedContent = z.infer<typeof identificationCardEnrichedContentSchema>;

export const identificationCardBlockKeys = identificationCardEnrichedContentSchema.keyof().options;
export type IdentificationCardBlockKey = (typeof identificationCardBlockKeys)[number];

// Insurance Card
export const insuranceCardEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.PRIMARY_COVERAGE]: nullableBlockShape[BlockKey.PRIMARY_COVERAGE],
  [BlockKey.SECONDARY_COVERAGE]: nullableBlockShape[BlockKey.SECONDARY_COVERAGE],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.INSURANCE]: nullableBlockShape[BlockKey.INSURANCE],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type InsuranceCardEnrichedContent = z.infer<typeof insuranceCardEnrichedContentSchema>;

export const insuranceCardBlockKeys = insuranceCardEnrichedContentSchema.keyof().options;
export type InsuranceCardBlockKey = (typeof insuranceCardBlockKeys)[number];

// Prior Authorization
export const priorAuthorizationEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.PRIOR_AUTHORIZATION]: nullableBlockShape[BlockKey.PRIOR_AUTHORIZATION],
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type PriorAuthorizationEnrichedContent = z.infer<typeof priorAuthorizationEnrichedContentSchema>;

export const priorAuthorizationBlockKeys = priorAuthorizationEnrichedContentSchema.keyof().options;
export type PriorAuthorizationBlockKey = (typeof priorAuthorizationBlockKeys)[number];

// Progress Note
export const progressNoteEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.PROGRESS_NOTE]: nullableBlockShape[BlockKey.PROGRESS_NOTE],
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ProgressNoteEnrichedContent = z.infer<typeof progressNoteEnrichedContentSchema>;

export const progressNoteBlockKeys = progressNoteEnrichedContentSchema.keyof().options;
export type ProgressNoteBlockKey = (typeof progressNoteBlockKeys)[number];

// Imaging Results
export const imagingResultsEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.IMAGING_RESULTS]: nullableBlockShape[BlockKey.IMAGING_RESULTS],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ImagingResultsEnrichedContent = z.infer<typeof imagingResultsEnrichedContentSchema>;

export const imagingResultsBlockKeys = imagingResultsEnrichedContentSchema.keyof().options;
export type ImagingResultsBlockKey = (typeof imagingResultsBlockKeys)[number];

// Lab Results
export const labResultsEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.LAB_RESULTS]: nullableBlockShape[BlockKey.LAB_RESULTS],
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type LabResultsEnrichedContent = z.infer<typeof labResultsEnrichedContentSchema>;

export const labResultsBlockKeys = labResultsEnrichedContentSchema.keyof().options;
export type LabResultsBlockKey = (typeof labResultsBlockKeys)[number];

// Consent — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated CONSENT block.
export const consentEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.CONSENT]: nullableBlockShape[BlockKey.CONSENT],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ConsentEnrichedContent = z.infer<typeof consentEnrichedContentSchema>;

// Operation Note — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated OPERATION_NOTE block.
export const operationNoteEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.OPERATION_NOTE]: nullableBlockShape[BlockKey.OPERATION_NOTE],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type OperationNoteEnrichedContent = z.infer<typeof operationNoteEnrichedContentSchema>;

// Intake — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated INTAKE block.
export const intakeEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.INTAKE]: nullableBlockShape[BlockKey.INTAKE],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type IntakeEnrichedContent = z.infer<typeof intakeEnrichedContentSchema>;

// Insurance — mirrors the generic entity-block layout but swaps DOCUMENT_INFORMATION for the curated INSURANCE block.
// Used by INSURANCE_COMMUNICATION and INSURANCE_LETTER_OF_ASSURANCE.
// REFERRAL_INFORMATION is included so insurance communications can carry case-matching signals (service
// line, diagnoses, body part, reason for referral). REFERRAL_INFORMATION is default-enabled globally, so
// it is explicitly DISABLED by default for both insurance classifications in default_blocks.yaml
// (byClassification) — otherwise every tenant's insurance docs would require Date of Referral. Only
// tenants that opt in (Confluent-All Star INSURANCE_COMMUNICATION, via referralInformation.enabled: true)
// extract it.
export const insuranceEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.REFERRAL_INFORMATION]: nullableBlockShape[BlockKey.REFERRAL_INFORMATION],
  [BlockKey.INSURANCE]: nullableBlockShape[BlockKey.INSURANCE],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type InsuranceEnrichedContent = z.infer<typeof insuranceEnrichedContentSchema>;

// Legal — mirrors the provider-field entity-block layout but swaps DOCUMENT_INFORMATION for the curated LEGAL block.
export const legalEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.LEGAL]: nullableBlockShape[BlockKey.LEGAL],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type LegalEnrichedContent = z.infer<typeof legalEnrichedContentSchema>;

// Legal Grievance — patient-only; no legal/provider/location block fields are extracted for this classification.
export const legalGrievanceEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type LegalGrievanceEnrichedContent = z.infer<typeof legalGrievanceEnrichedContentSchema>;

// Clearance Request
export const clearanceRequestEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.CLEARANCE_REQUEST]: nullableBlockShape[BlockKey.CLEARANCE_REQUEST],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ClearanceRequestEnrichedContent = z.infer<typeof clearanceRequestEnrichedContentSchema>;

// Financial
export const financialEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.FINANCIAL]: nullableBlockShape[BlockKey.FINANCIAL],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type FinancialEnrichedContent = z.infer<typeof financialEnrichedContentSchema>;

// Procedure
export const procedureEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.PROCEDURE]: nullableBlockShape[BlockKey.PROCEDURE],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type ProcedureEnrichedContent = z.infer<typeof procedureEnrichedContentSchema>;

// Generic
export const genericEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});

const genericShape = genericEnrichedContentSchema.shape;

export const adminEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.ADMIN]: nullableBlockShape[BlockKey.ADMIN],
});
export const providerFieldEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
});
// Status requests additionally extract the referring provider (the office requesting the update).
export const referralStatusRequestEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.REFERRING_PROVIDER]: nullableBlockShape[BlockKey.REFERRING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
});
export const medicalRecordRequestEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.MEDICAL_RECORD_REQUEST]: nullableBlockShape[BlockKey.MEDICAL_RECORD_REQUEST],
});
export const referralResponseEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.REFERRAL_RESPONSE]: nullableBlockShape[BlockKey.REFERRAL_RESPONSE],
});
export const dmeEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.DME]: nullableBlockShape[BlockKey.DME],
});
export const notificationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.NOTIFICATION]: nullableBlockShape[BlockKey.NOTIFICATION],
});
// Off by default for discharge notices (default_blocks.yaml); groups opt in.
export const hospitalDischargeEnrichedContentSchema = notificationEnrichedContentSchema.extend({
  [BlockKey.EMERGENCY_SUMMARY]: nullableBlockShape[BlockKey.EMERGENCY_SUMMARY],
});
export const emergencySummaryEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.EMERGENCY_SUMMARY]: nullableBlockShape[BlockKey.EMERGENCY_SUMMARY],
});
export const carePlanEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.CARE_PLAN]: nullableBlockShape[BlockKey.CARE_PLAN],
});
export const referrerInformationEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export const medicalHistoryEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.MEDICAL_HISTORY]: nullableBlockShape[BlockKey.MEDICAL_HISTORY],
});
export const progressNoteWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.PROGRESS_NOTE]: nullableBlockShape[BlockKey.PROGRESS_NOTE],
});
export const imagingResultsWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.IMAGING_RESULTS]: nullableBlockShape[BlockKey.IMAGING_RESULTS],
});
export const continuityOfCareWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.CONTINUITY_OF_CARE]: nullableBlockShape[BlockKey.CONTINUITY_OF_CARE],
});
export const labResultsWithDocumentInformationEnrichedContentSchema = z.object({
  ...genericShape,
  [BlockKey.LAB_RESULTS]: nullableBlockShape[BlockKey.LAB_RESULTS],
});

export const referralRequestEnrichedContentSchema = z.object({
  ...genericEnrichedContentSchema.shape,
  [BlockKey.REFERRING_PROVIDER]: nullableBlockShape[BlockKey.REFERRING_PROVIDER],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.PRIMARY_COVERAGE]: nullableBlockShape[BlockKey.PRIMARY_COVERAGE],
  [BlockKey.REFERRAL_INFORMATION]: nullableBlockShape[BlockKey.REFERRAL_INFORMATION],
});

// Pharmacy Related
export const pharmacyRelatedEnrichedContentSchema = z.object({
  [BlockKey.PATIENT]: nullableBlockShape[BlockKey.PATIENT],
  [BlockKey.ORDERING_PROVIDER]: nullableBlockShape[BlockKey.ORDERING_PROVIDER],
  [BlockKey.PROVIDER]: nullableBlockShape[BlockKey.PROVIDER],
  [BlockKey.RECEIVING_PROVIDER]: nullableBlockShape[BlockKey.RECEIVING_PROVIDER],
  [BlockKey.REFERRING_LOCATION]: nullableBlockShape[BlockKey.REFERRING_LOCATION],
  [BlockKey.RECEIVING_LOCATION]: nullableBlockShape[BlockKey.RECEIVING_LOCATION],
  [BlockKey.DOCUMENT_INFORMATION]: nullableBlockShape[BlockKey.DOCUMENT_INFORMATION],
  [BlockKey.PATIENT_SUPPLEMENTAL]: nullableBlockShape[BlockKey.PATIENT_SUPPLEMENTAL],
});
export type GenericEnrichedContent = z.infer<typeof genericEnrichedContentSchema>;

export const genericBlockKeys = genericEnrichedContentSchema.keyof().options;
export type GenericBlockKey = (typeof genericBlockKeys)[number];
export type PharmacyRelatedEnrichedContent = z.infer<typeof pharmacyRelatedEnrichedContentSchema>;

export const pharmacyRelatedWithDocumentInformationEnrichedContentSchema = z.object({
  ...pharmacyRelatedEnrichedContentSchema.shape,
  [BlockKey.PHARMACY_RELATED]: nullableBlockShape[BlockKey.PHARMACY_RELATED],
});

export const enrichedContentUnionSchema: z.ZodType<EnrichedContentBlocks | null, unknown> = z
  .union([
    adminEnrichedContentSchema,
    referralWithDocumentInformationEnrichedContentSchema,
    referralEnrichedContentSchema,
    identificationCardEnrichedContentSchema,
    insuranceCardEnrichedContentSchema,
    priorAuthorizationEnrichedContentSchema,
    progressNoteEnrichedContentSchema,
    progressNoteWithDocumentInformationEnrichedContentSchema,
    imagingResultsWithDocumentInformationEnrichedContentSchema,
    continuityOfCareWithDocumentInformationEnrichedContentSchema,
    labResultsWithDocumentInformationEnrichedContentSchema,
    imagingResultsEnrichedContentSchema,
    labResultsEnrichedContentSchema,
    pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
    pharmacyRelatedEnrichedContentSchema,
    providerFieldEnrichedContentSchema,
    medicalRecordRequestEnrichedContentSchema,
    referralResponseEnrichedContentSchema,
    referralRequestEnrichedContentSchema,
    dmeEnrichedContentSchema,
    hospitalDischargeEnrichedContentSchema,
    notificationEnrichedContentSchema,
    emergencySummaryEnrichedContentSchema,
    carePlanEnrichedContentSchema,
    medicalHistoryEnrichedContentSchema,
    consentEnrichedContentSchema,
    operationNoteEnrichedContentSchema,
    intakeEnrichedContentSchema,
    insuranceEnrichedContentSchema,
    legalEnrichedContentSchema,
    clearanceRequestEnrichedContentSchema,
    financialEnrichedContentSchema,
    procedureEnrichedContentSchema,
    genericEnrichedContentSchema,
  ])
  .nullable();

export type EnrichedContent = z.infer<typeof enrichedContentUnionSchema>;
// Unvalidated eligibility input; typed review/rendering code uses EnrichedContentBlocks.
export type EnrichedContentIndexable = Partial<Record<BlockKey, unknown>>;

export const ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION = {
  [Classification.ADMIN]: adminEnrichedContentSchema,
  [Classification.ADMIN_DISABILITY_FORMS]: adminEnrichedContentSchema,
  [Classification.OTHER]: adminEnrichedContentSchema,
  [Classification.REFERRAL]: referralWithDocumentInformationEnrichedContentSchema,
  [Classification.REFERRAL_AUTH]: referralEnrichedContentSchema,
  [Classification.REFERER_INFORMATION]: referrerInformationEnrichedContentSchema,
  [Classification.REFERRAL_RESPONSE]: referralResponseEnrichedContentSchema,
  [Classification.REFERRAL_REQUEST]: referralRequestEnrichedContentSchema,
  [Classification.CONSENT_FOR_RECORDS_SPECIALIST]: referralRequestEnrichedContentSchema,
  [Classification.RESPONSE_TO_VH_REQUEST]: referralEnrichedContentSchema,
  [Classification.IDENTIFICATION_CARD]: identificationCardEnrichedContentSchema,
  [Classification.ADMIN_LEAD_FORM]: providerFieldEnrichedContentSchema,
  [Classification.INSURANCE_CARD]: insuranceCardEnrichedContentSchema,
  [Classification.INSURANCE_COMMUNICATION]: insuranceEnrichedContentSchema,
  [Classification.INSURANCE_LETTER_OF_ASSURANCE]: insuranceEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_IMAGING_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_SURGERY_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_SURGERY_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_THERAPY_REQUEST]: priorAuthorizationEnrichedContentSchema,
  [Classification.PRIOR_AUTHORIZATION_THERAPY_RESPONSE]: priorAuthorizationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE]: progressNoteEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_PT]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_ER_REPORT]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_HOSPITAL_DISCHARGE_SUMMARY]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_HOSPITAL_RECORDS]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_VITAL_CHART]: progressNoteWithDocumentInformationEnrichedContentSchema,
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: providerFieldEnrichedContentSchema,
  [Classification.CONTINUITY_OF_CARE]: continuityOfCareWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS]: imagingResultsEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CT]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ENDOSCOPY]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CARDIAC]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_STRESS_TEST]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ULTRASOUND]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_X_RAY]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_DEXA]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ECHO]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EEG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EKG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_EMG]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_ESOPHAGRAM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_HOLTER_MONITOR]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_MAMMOGRAM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_MRI]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_PULMONARY_FUNCTION]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_RETINAL]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_NM]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_PET]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING]: imagingResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.LAB_RESULTS]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_BP]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.LAB_RESULTS_ORDER_FORM]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED]: pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CARE_CONSIDERATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CLARIFICATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_CONTROLED_SUBSTANCES_POLICY]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_DRUG_MONITORING_PROGRAM]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_MED_RECONCILIATION]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_MEDICATION_LIST]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_NON_ADHERENCE]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_REFILL]: pharmacyRelatedWithDocumentInformationEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_REFILL_MANY]: pharmacyRelatedEnrichedContentSchema,
  [Classification.PHARMACY_RELATED_ALERT]: pharmacyRelatedEnrichedContentSchema,
  [Classification.ORDER_LAB]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_IMAGING]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_SIGNED]: providerFieldEnrichedContentSchema,
  [Classification.ORDER_CLARIFICATION]: providerFieldEnrichedContentSchema,
  [Classification.DME_ORDER]: dmeEnrichedContentSchema,
  [Classification.FINANCIAL]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_BILLING_RECORDS]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS]: financialEnrichedContentSchema,
  [Classification.FINANCIAL_ELIGIBILITY_OF_BENEFITS_CREDIT_CARD]: financialEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_RESPONSE]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_PRE_OP]: clearanceRequestEnrichedContentSchema,
  [Classification.CLEARANCE_REQUEST_SURGICAL]: clearanceRequestEnrichedContentSchema,
  [Classification.PROCEDURE]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_CARDIAC]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_CARDIAC_VEIN]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_REHAB]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_DEVICE_REQUEST]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_DEVICE_INFO]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_GI]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_INVENTORY_LIST]: procedureEnrichedContentSchema,
  [Classification.PROCEDURE_SLEEP]: procedureEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_LEGAL]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_OTHER]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_SSA]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_RECORD_REQUEST_RESPONSE]: medicalRecordRequestEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_HOME_HEALTH]: providerFieldEnrichedContentSchema,
  [Classification.REFERRAL_STATUS_REQUEST]: referralStatusRequestEnrichedContentSchema,
  [Classification.DME_REQUEST]: dmeEnrichedContentSchema,
  [Classification.DME_RESPONSE]: dmeEnrichedContentSchema,
  [Classification.NOTIFICATION]: notificationEnrichedContentSchema,
  [Classification.NOTIFICATION_HOSPITAL_ADMIT]: notificationEnrichedContentSchema,
  [Classification.NOTIFICATION_HOSPITAL_DISCHARGE]: hospitalDischargeEnrichedContentSchema,
  [Classification.EMERGENCY_SUMMARY]: emergencySummaryEnrichedContentSchema,
  [Classification.CARE_PLAN]: carePlanEnrichedContentSchema,
  [Classification.CARE_PLAN_REMINDER]: carePlanEnrichedContentSchema,
  [Classification.CONSENT]: consentEnrichedContentSchema,
  [Classification.OPERATION_NOTE]: operationNoteEnrichedContentSchema,
  [Classification.INTAKE]: intakeEnrichedContentSchema,
  [Classification.INTAKE_DIALYSIS_QUESTIONAIRE]: intakeEnrichedContentSchema,
  [Classification.LEGAL]: legalEnrichedContentSchema,
  [Classification.LEGAL_ADVANCED_CARE_DIRECTIVE]: legalEnrichedContentSchema,
  [Classification.LEGAL_COURT_DOCUMENTS]: legalEnrichedContentSchema,
  [Classification.LEGAL_GRIEVANCE]: legalGrievanceEnrichedContentSchema,
  [Classification.LEGAL_POWER_OF_ATTORNEY]: legalEnrichedContentSchema,
  [Classification.LEGAL_SUBPOENA]: legalEnrichedContentSchema,
  [Classification.LAB_RESULTS_OTHER]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_PATHOLOGY]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_BILIRUBIN]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_HEARING_TEST]: labResultsEnrichedContentSchema,
  [Classification.LAB_RESULTS_PKU]: labResultsEnrichedContentSchema,
  // Keep documentInformation for tenants that configure it; PA Central uses the labResults block.
  [Classification.LAB_RESULTS_COLOGUARD]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.LAB_RESULTS_PAP_SMEAR]: labResultsWithDocumentInformationEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_RESPONSE]: medicalHistoryEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY]: medicalHistoryEnrichedContentSchema,
  [Classification.MEDICAL_HISTORY_IMMUNIZATION]: medicalHistoryEnrichedContentSchema,
  [Classification.ORDER_FROM_PHYSICIAN]: adminEnrichedContentSchema,
} satisfies Partial<Record<Classification, z.ZodObject>>;

export function getEnrichedContentSchemaForClassification(classification: Classification) {
  const schemas: Partial<Record<Classification, z.ZodObject<Partial<NullableBlockShape>>>> =
    ENRICHED_CONTENT_SCHEMA_BY_CLASSIFICATION;

  return schemas[classification] ?? genericEnrichedContentSchema;
}

export function getBlockKeysForClassification(classification: Classification): readonly BlockKey[] {
  const schema = getEnrichedContentSchemaForClassification(classification);

  return schema.keyof().options;
}
